import {
  evaluateGprRule,
  useSiteHealthConfig,
} from "@inrange/building-manager-api-client";
import { Site } from "@inrange/building-manager-api-client/models-site";
import { Badge, Button, Spinner } from "react-bootstrap";

const describeGprRule = (
  rule: NonNullable<
    ReturnType<typeof useSiteHealthConfig>["healthConfig"]["data"]
  >["gprRule"]
): string => {
  if (rule == null) return "not set";
  if (rule.type === "flat") return `${rule.valuePct}% flat`;
  if (rule.type === "fractionOfDesignedPr")
    return `${(rule.fraction * 100).toFixed(1)}% of Designed PR`;
  if (rule.type === "designedPrMinus")
    return `Designed PR − ${rule.deltaPct}%`;
  const [a, b] = rule.terms;
  const describeTerm = (t: typeof a): string =>
    t.type === "flat"
      ? `${t.valuePct}%`
      : t.type === "fractionOfDesignedPr"
        ? `${(t.fraction * 100).toFixed(1)}% of Designed PR`
        : `Designed PR − ${t.deltaPct}%`;
  return `${rule.type}(${describeTerm(a)}, ${describeTerm(b)})`;
};

const HealthMonitoringConfigSection = ({
  site,
  onConfigureHealth,
}: {
  site: Site;
  onConfigureHealth: () => void;
}) => {
  const { healthConfig } = useSiteHealthConfig(site.id);
  const bmaConfig = healthConfig.data;

  return (
    <div>
      <h6 className="fw-bold mb-2">Health config</h6>
      <div className="d-flex align-items-center gap-2 mb-1">
        {healthConfig.isLoading ? (
          <Spinner animation="border" size="sm" />
        ) : bmaConfig ? (
          <>
            <Badge bg="success">Configured</Badge>
            <Button
              variant="secondary"
              size="sm"
              onClick={onConfigureHealth}
            >
              Edit
            </Button>
          </>
        ) : (
          <>
            <Badge bg="warning" text="dark">
              Not configured
            </Badge>
            <Button
              variant="primary"
              size="sm"
              onClick={onConfigureHealth}
            >
              Configure
            </Button>
          </>
        )}
      </div>
      {bmaConfig && (
        <div className="text-muted small d-flex gap-3 flex-wrap">
          <span>
            Alerting: {bmaConfig.alertingEnabled ? "enabled" : "disabled"}
          </span>
          <span>
            Late data threshold: {bmaConfig.lateDataThresholdSeconds / 3600}h
          </span>
          {bmaConfig.unreliableConnection && (
            <span>Unreliable connection</span>
          )}
          {bmaConfig.contractedAvailability != null && (
            <span>
              Contracted availability: {bmaConfig.contractedAvailability}%
            </span>
          )}
          {bmaConfig.designedPrPct != null && (
            <span>Designed PR: {bmaConfig.designedPrPct}%</span>
          )}
          {bmaConfig.gprRule != null &&
            (() => {
              const evaluated = evaluateGprRule(
                bmaConfig.gprRule,
                bmaConfig.designedPrPct
              );
              return (
                <span>
                  Guaranteed PR:{" "}
                  {evaluated != null ? `${evaluated.toFixed(1)}%` : "—"} (
                  {describeGprRule(bmaConfig.gprRule)})
                </span>
              );
            })()}
        </div>
      )}
    </div>
  );
};

export default HealthMonitoringConfigSection;
